import { useState, useEffect } from "react";
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Badge,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { formatDistanceToNow } from "date-fns";
import { getNotifications } from "../api/getCommands";
import { readNotification } from "../api/postCommands";

interface NotificationItem {
  id: number;
  notificationId: number;
  message: string;
  timestamp: string;
  read: boolean;
}

export default function Notifications() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    getNotifications()
      .then((data) => {
        const readIds = JSON.parse(localStorage.getItem("readNotifications") || "[]") as number[];
        const mapped = data.map((note, index) => ({
          id: index,
          notificationId: note.Id,
          message: note.notification_message,
          timestamp: note.postedDate,
          read: readIds.includes(note.Id),
        }));
        mapped.reverse();
        setNotifications(mapped);
        setUnreadCount(mapped.filter((n) => !n.read).length);
      })
      .catch(() => {});
  }, []);

  function handleOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);

    // Mark all as read
    const newReadIds = notifications.filter((n) => !n.read).map((n) => n.notificationId);
    if (newReadIds.length > 0) {
      readNotification(newReadIds);
      const allReadIds = notifications.map((n) => n.notificationId);
      localStorage.setItem("readNotifications", JSON.stringify(allReadIds));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }

  return (
    <>
      <IconButton id="notifications-btn" onClick={handleOpen} color="inherit" size="small" sx={{ color: "grey.400" }}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        id="notifications-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <List id="notifications-list" sx={{ width: 300, maxHeight: 400, overflowY: "auto" }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: "gray", textAlign: "center" }}>
                    No notifications
                  </Typography>
                }
              />
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  "&:hover": { backgroundColor: "#444", cursor: "pointer" },
                  padding: "8px 16px",
                  transition: "background-color 0.3s ease",
                }}
              >
                <ListItemText
                  primary={notification.message}
                  secondary={
                    <Typography variant="body2" sx={{ color: "gray" }}>
                      {formatDistanceToNow(new Date(notification.timestamp))} ago
                    </Typography>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}
